import React, { useEffect, useMemo, useState } from 'react';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { Input, Tag } from 'antd';
import { useNavigate } from 'react-router-dom';
import config from '../../config';
import axios from 'axios';
import {
  NdoePage, NdoePageHeader, NdoeActionButton, NdoeImage,
  NdoeResponsiveTable, NdoeEmpty, NdoeLoading, NdoeError,
  NdoeArticleLink, useFicheArticle,
} from '../../components/ndoe';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;

// Retours clients — RECONSTRUITE (M5, 2026-07-11). L'ancienne page était
// cassée de bout en bout : la liste lisait GET /api/vente/retour (capté par
// la route /:id → toujours vide), la recherche était décorative, la
// modification tapait une route inexistante et « Supprimer » appelait
// /api/peuple/clientDelete — il SOFT-SUPPRIMAIT UN CLIENT dont l'id valait
// celui du retour. Désormais : lecture des retours réels (mouvements type 5)
// via GET /api/vente/retours, recherche qui filtre vraiment, référence
// produit partout ; les actions dangereuses n'existent plus (un retour est
// un mouvement de stock — il se corrige par mouvement inverse, pas par
// suppression).
const Retour = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [q, setQ] = useState('');
  const { openFiche, ficheElement } = useFicheArticle();

  const charger = async () => {
    setLoading(true);
    setErreur(null);
    try {
      const { data } = await axios.get(`${DOMAIN}/api/vente/retours`);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setErreur(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { charger(); }, []);

  const filtres = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter((r) =>
      [r.nom_produit, r.code_produit, r.code_article, r.nom_client, r.username, r.description]
        .some((v) => v && String(v).toLowerCase().includes(t)));
  }, [rows, q]);

  const colonnes = [
    {
      title: 'Article',
      dataIndex: 'nom_produit',
      key: 'article',
      render: (text, r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <NdoeImage src={r.img} alt={r.nom_produit || 'Article'} size="md" />
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span className="num" style={{ color: '#8a5a10', fontWeight: 700, fontSize: 12 }}>
              {r.code_article || r.code_produit || '—'}
            </span>
            <NdoeArticleLink onClick={() => openFiche(r.id_produit)}>{r.nom_produit || '—'}</NdoeArticleLink>
            {r.taille ? <small style={{ color: '#64748b' }}>P{r.taille}</small> : null}
          </div>
        </div>
      ),
    },
    { title: 'Qté', dataIndex: 'quantite', key: 'quantite', render: (v) => <Tag color="green">{v}</Tag> },
    {
      title: 'État',
      dataIndex: 'avarie',
      key: 'avarie',
      render: (v) => (Number(v) ? <Tag color="red">Avarie</Tag> : <Tag>Remis en stock</Tag>),
    },
    { title: 'Cliente', dataIndex: 'nom_client', key: 'client', render: (v) => v || '—' },
    { title: 'Motif', dataIndex: 'description', key: 'motif', render: (v) => v || '—' },
    {
      title: 'Quand',
      dataIndex: 'date_mouvement',
      key: 'date',
      render: (v) => (v ? new Date(v).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—'),
    },
    { title: 'Par', dataIndex: 'username', key: 'user', render: (v) => v || '—' },
  ];

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Après-vente"
        title="Retours clients"
        subtitle="Chaque retour est un mouvement de stock tracé (remise en stock ou avarie)."
        actions={(
          <NdoeActionButton variant="primary" icon={<PlusOutlined />} onClick={() => navigate('/retourForm')}>
            Nouveau retour
          </NdoeActionButton>
        )}
      />

      <div style={{ maxWidth: 420, margin: '0 0 12px' }}>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Référence, article, cliente, motif…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {loading ? (
        <NdoeLoading rows={4} />
      ) : erreur ? (
        <NdoeError text={erreur} onRetry={charger} />
      ) : !filtres.length ? (
        <NdoeEmpty
          icon="↩️"
          title={q ? 'Aucun retour ne correspond' : 'Aucun retour enregistré'}
          text={q ? 'Essayez une autre recherche.' : 'Les retours validés apparaîtront ici.'}
        />
      ) : (
        <NdoeResponsiveTable
          columns={colonnes}
          data={filtres}
          rowKey="id_mouvement"
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          renderCard={(r) => (
            <div className="ndoe-card" style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
              <NdoeImage src={r.img} alt={r.nom_produit || 'Article'} size="md" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="num" style={{ color: '#8a5a10', fontWeight: 700, fontSize: 12 }}>
                  {r.code_article || r.code_produit || '—'}
                </div>
                <b style={{ display: 'block', color: '#071c3d', fontSize: 13.5 }}>{r.nom_produit}</b>
                <small style={{ color: '#64748b' }}>
                  {[r.taille ? `P${r.taille}` : null, r.nom_client, r.description].filter(Boolean).join(' · ')}
                </small>
                <div style={{ marginTop: 4, display: 'flex', gap: 6, alignItems: 'center' }}>
                  <Tag color="green">{r.quantite}</Tag>
                  {Number(r.avarie) ? <Tag color="red">Avarie</Tag> : <Tag>Remis en stock</Tag>}
                  <small style={{ color: '#9aa6b6' }}>
                    {r.date_mouvement ? new Date(r.date_mouvement).toLocaleDateString('fr-FR') : ''}
                  </small>
                </div>
              </div>
            </div>
          )}
        />
      )}
      {ficheElement}
    </NdoePage>
  );
};

export default Retour;
